import { useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Building2, Globe, Mail, MapPin, Phone, Plus, Trash2, UserRound, X, Search } from "lucide-react"
import { toast } from "@/components/portal/Toast"
import { FeldText, NotizFeld, Verlauf } from "@/components/vertrieb/DealBlatt"
import {
  type Aktivitaet, type Board, type Deal, type Kontakt,
  crmFetch, euro, initialen, kontaktName, personFarbe, vorWann,
} from "@/lib/crm"

/**
 * Kontakte im Vertrieb (Paul 26.10.2026: „Wo sehe ich die kontakte und kann
 * neue anlegen? Ich brauch ja sowas wie kontakte nicht nur die pipeline").
 *
 * Ein Kontakt ist ein Mensch (mit Firma). Deals hängen daran; Name, Mail und
 * Telefon eines Deals kommen vom Kontakt — wer sie am Deal ändert, ändert den
 * Kontakt.
 */

export const QUELLEN = ["Website", "Empfehlung", "Telefon", "LinkedIn", "Messe", "Kaltakquise", "Sonstiges"]

export function useKontakte() {
  return useQuery<{ items: Kontakt[] }>({
    queryKey: ["crm-kontakte"],
    queryFn: () => crmFetch("/kontakte"),
  })
}

export function useZustaendige() {
  return useQuery<{ items: string[] }>({
    queryKey: ["portal-aufgaben-zustaendige"],
    queryFn: async () => (await fetch("/api/v1/portal-aufgaben/zustaendige", { credentials: "include" })).json(),
    staleTime: 600_000,
  })
}

function Avatar({ k, gross }: { k: Pick<Kontakt, "name" | "firma">; gross?: boolean }) {
  const n = kontaktName(k)
  return (
    <span className={`vt-avatar ${gross ? "gross" : ""}`} style={{ background: personFarbe(n) }}>
      {initialen(n)}
    </span>
  )
}

// ── Liste ────────────────────────────────────────────────────────────────

export function KontakteAnsicht({ suche, board, oeffne, neu }: {
  suche: string; board: Board; oeffne: (id: number) => void; neu: () => void
}) {
  const q = useKontakte()
  const stufeVon = useMemo(() => Object.fromEntries(board.stufen.map((s) => [s.schluessel, s])), [board.stufen])
  const liste = useMemo(() => {
    const t = suche.trim().toLowerCase()
    return (q.data?.items || []).filter((k) =>
      !t || [k.name, k.firma, k.email, k.telefon, k.ort].some((x) => (x || "").toLowerCase().includes(t)))
  }, [q.data, suche])

  if (q.isLoading) return <div className="vt-hinweis">Lädt …</div>
  if (!q.data?.items.length) {
    return (
      <div className="vt-leer">
        <div className="vt-leer-bild"><UserRound /></div>
        <h3>Noch keine Kontakte</h3>
        <p>Leg Menschen an, mit denen ihr sprecht – Deals lassen sich danach direkt daran hängen.</p>
        <button className="vt-knopf haupt" onClick={neu}><Plus /> Ersten Kontakt anlegen</button>
      </div>
    )
  }
  return (
    <div className="vt-liste">
      <table>
        <thead>
          <tr><th>Name</th><th>E-Mail</th><th>Telefon</th><th>Deals</th><th>Phase</th><th>Zuständig</th><th>Zuletzt</th></tr>
        </thead>
        <tbody>
          {liste.map((k) => {
            const s = k.letzte_stufe ? stufeVon[k.letzte_stufe] : null
            return (
              <tr key={k.id} onClick={() => oeffne(k.id)}>
                <td>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <Avatar k={k} />
                    <div>
                      <b>{kontaktName(k)}</b>
                      {k.name && k.firma && <div className="vt-hinweis">{k.firma}{k.position ? ` · ${k.position}` : ""}</div>}
                    </div>
                  </div>
                </td>
                <td className="leise">{k.email || "–"}</td>
                <td className="leise">{k.telefon || "–"}</td>
                <td>{k.deals ? <>{k.deals}{k.offen_wert_monat ? <span className="vt-hinweis"> · {euro(k.offen_wert_monat)}/Mon.</span> : null}</> : <span className="vt-hinweis">–</span>}</td>
                <td>{s ? <span className="vt-pille"><span className="vt-punkt" style={{ background: s.farbe || "#94a3b8" }} />{s.label}</span> : <span className="vt-hinweis">–</span>}</td>
                <td className="leise">{k.zustaendig || "–"}</td>
                <td className="leise">{k.letzte_aktivitaet ? vorWann(k.letzte_aktivitaet) : "–"}</td>
              </tr>
            )
          })}
          {!liste.length && <tr><td colSpan={7} className="leise">Kein Kontakt passt zu „{suche}“.</td></tr>}
        </tbody>
      </table>
    </div>
  )
}

// ── Neuer Kontakt ────────────────────────────────────────────────────────

export function NeuerKontakt({ ich, zu, angelegt }: { ich: string; zu: () => void; angelegt: (k: Kontakt) => void }) {
  const qc = useQueryClient()
  const zust = useZustaendige()
  const [f, setF] = useState({ name: "", firma: "", position: "", email: "", telefon: "", quelle: "", zustaendig: ich })
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value })
  const m = useMutation({
    mutationFn: () => crmFetch<Kontakt>("/kontakte", { method: "POST", json: f }),
    onSuccess: (k) => { qc.invalidateQueries({ queryKey: ["crm-kontakte"] }); toast(`„${kontaktName(k)}" angelegt`); angelegt(k) },
    onError: (e: Error) => toast(e.message, "error"),
  })
  useEsc(zu)
  const ok = f.name.trim() || f.firma.trim()
  return createPortal(
    <div className="vt-portal">
      <div className="vt-schleier" onClick={zu} />
      <form className="vt-dialog" role="dialog" aria-label="Neuer Kontakt" onSubmit={(e) => { e.preventDefault(); if (ok) m.mutate() }}>
        <div className="vt-dialog-kopf">
          <h3>Neuer Kontakt</h3>
          <p>Name oder Firma reicht.</p>
        </div>
        <div className="vt-dialog-rumpf">
          <div className="vt-felder">
            <div className="vt-feld"><label>Name</label><input autoFocus value={f.name} onChange={set("name")} placeholder="Vor- und Nachname" /></div>
            <div className="vt-feld"><label>Firma</label><input value={f.firma} onChange={set("firma")} /></div>
            <div className="vt-feld"><label>E-Mail</label><input type="email" value={f.email} onChange={set("email")} /></div>
            <div className="vt-feld"><label>Telefon</label><input type="tel" value={f.telefon} onChange={set("telefon")} /></div>
            <div className="vt-feld"><label>Position</label><input value={f.position} onChange={set("position")} placeholder="z. B. Inhaber" /></div>
            <div className="vt-feld"><label>Quelle</label>
              <select value={f.quelle} onChange={set("quelle")}><option value="">–</option>{QUELLEN.map((q) => <option key={q}>{q}</option>)}</select></div>
            <div className="vt-feld breit"><label>Zuständig</label>
              <select value={f.zustaendig} onChange={set("zustaendig")}>
                <option value="">niemand</option>
                {Array.from(new Set([ich, ...(zust.data?.items || [])].filter(Boolean))).map((n) => <option key={n}>{n}</option>)}
              </select></div>
          </div>
        </div>
        <div className="vt-dialog-fuss">
          <button type="button" className="vt-knopf leise" onClick={zu}>Abbrechen</button>
          <button type="submit" className="vt-knopf haupt" disabled={!ok || m.isPending}>{m.isPending ? "Legt an …" : "Kontakt anlegen"}</button>
        </div>
      </form>
    </div>,
    document.body,
  )
}

function useEsc(zu: () => void) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return
      const t = e.target as HTMLElement
      if (t && ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName)) { t.blur(); return }
      zu()
    }
    window.addEventListener("keydown", esc)
    return () => window.removeEventListener("keydown", esc)
  }, [zu])
}

// ── Kontakt-Blatt ────────────────────────────────────────────────────────

type KontaktDetail = { kontakt: Kontakt; deals: Deal[]; verlauf: Aktivitaet[] }

export function KontaktBlatt({ kontaktId, board, zu, oeffneDeal, neuerDeal }: {
  kontaktId: number; board: Board; zu: () => void; oeffneDeal: (id: number) => void; neuerDeal: (k: Kontakt) => void
}) {
  const qc = useQueryClient()
  const zust = useZustaendige()
  const q = useQuery<KontaktDetail>({ queryKey: ["crm-kontakt", kontaktId], queryFn: () => crmFetch(`/kontakte/${kontaktId}`) })
  useEsc(zu)
  const aktualisiere = () => {
    qc.invalidateQueries({ queryKey: ["crm-kontakt", kontaktId] })
    qc.invalidateQueries({ queryKey: ["crm-kontakte"] })
    qc.invalidateQueries({ queryKey: ["crm-board"] })
  }
  const speichern = useMutation({
    mutationFn: (daten: Partial<Kontakt>) => crmFetch(`/kontakte/${kontaktId}`, { method: "PATCH", json: daten }),
    onSuccess: aktualisiere,
    onError: (e: Error) => toast(e.message, "error"),
  })
  const loeschen = useMutation({
    mutationFn: () => crmFetch(`/kontakte/${kontaktId}`, { method: "DELETE" }),
    onSuccess: () => { aktualisiere(); toast("Kontakt gelöscht"); zu() },
    onError: (e: Error) => toast(e.message, "error"),
  })
  const k = q.data?.kontakt
  const stufeVon = Object.fromEntries(board.stufen.map((s) => [s.schluessel, s]))
  const leer = (v: string) => v.trim() || null

  return createPortal(
    <div className="vt-portal">
      <div className="vt-schleier" onClick={zu} />
      <aside className="vt-blatt" role="dialog" aria-label="Kontakt">
        {!k ? (
          <div className="vt-blatt-inhalt"><span className="vt-hinweis">{q.error ? (q.error as Error).message : "Lädt …"}</span></div>
        ) : (
          <>
            <div className="vt-blatt-kopf">
              <div className="vt-blatt-zeile">
                <Avatar k={k} gross />
                <div className="vt-blatt-titel">
                  <FeldText key={`n${k.id}`} className="vt-feld-titel" wert={k.name} aria="Name" platzhalter="Name"
                    speichere={(v) => speichern.mutate({ name: leer(v) })} />
                  <div className="vt-blatt-unter">
                    {k.firma && <span><Building2 /> {k.firma}{k.position ? ` · ${k.position}` : ""}</span>}
                    {k.telefon && <a href={`tel:${k.telefon.replace(/[^\d+]/g, "")}`}><Phone /> {k.telefon}</a>}
                    {k.email && <a href={`mailto:${k.email}`}><Mail /> {k.email}</a>}
                  </div>
                </div>
                <button className="vt-ikon" onClick={zu} title="Schließen (Esc)"><X /></button>
              </div>
            </div>

            <div className="vt-blatt-inhalt">
              <section className="vt-block">
                <div className="vt-block-kopf">
                  <h4>Deals {q.data!.deals.length ? `· ${q.data!.deals.length}` : ""}</h4>
                  <button className="vt-knopf leise klein" onClick={() => neuerDeal(k)}><Plus /> Deal</button>
                </div>
                <div className="vt-block-rumpf">
                  {q.data!.deals.length === 0 ? (
                    <span className="vt-hinweis">Noch kein Deal mit {kontaktName(k)}.</span>
                  ) : (
                    <ul className="vt-aufgaben">
                      {q.data!.deals.map((d) => {
                        const s = stufeVon[d.stufe]
                        return (
                          <li key={d.id} className="vt-aufgabe" style={{ cursor: "pointer" }} onClick={() => oeffneDeal(d.id)}>
                            <span className="vt-punkt" style={{ background: s?.farbe || "#94a3b8" }} />
                            <span className="vt-aufgabe-text"><b style={{ fontWeight: 600 }}>{d.titel}</b></span>
                            {d.wert_monat ? <span className="vt-hinweis">{euro(d.wert_monat)}/Mon.</span> : null}
                            <span className={`vt-pille ${s?.art === "gewonnen" ? "gruen" : ""}`}>{s?.label || d.stufe}</span>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </div>
              </section>

              <section className="vt-block">
                <div className="vt-block-kopf"><h4>Details</h4></div>
                <div className="vt-block-rumpf">
                  <div className="vt-felder">
                    <div className="vt-feld"><label>Firma</label><FeldText wert={k.firma} speichere={(v) => speichern.mutate({ firma: leer(v) })} /></div>
                    <div className="vt-feld"><label>Position</label><FeldText wert={k.position} speichere={(v) => speichern.mutate({ position: leer(v) })} /></div>
                    <div className="vt-feld"><label><Mail size={12} /> E-Mail</label><FeldText typ="email" wert={k.email} speichere={(v) => speichern.mutate({ email: leer(v) })} /></div>
                    <div className="vt-feld"><label><Phone size={12} /> Telefon</label><FeldText typ="tel" wert={k.telefon} speichere={(v) => speichern.mutate({ telefon: leer(v) })} /></div>
                    <div className="vt-feld"><label><Globe size={12} /> Website</label><FeldText wert={k.website} speichere={(v) => speichern.mutate({ website: leer(v) })} /></div>
                    <div className="vt-feld"><label><MapPin size={12} /> Ort</label><FeldText wert={k.ort} speichere={(v) => speichern.mutate({ ort: leer(v) })} /></div>
                    <div className="vt-feld"><label>Quelle</label>
                      <select value={k.quelle || ""} onChange={(e) => speichern.mutate({ quelle: e.target.value || null })}>
                        <option value="">–</option>
                        {Array.from(new Set([k.quelle, ...QUELLEN].filter(Boolean) as string[])).map((x) => <option key={x}>{x}</option>)}
                      </select></div>
                    <div className="vt-feld"><label>Zuständig</label>
                      <select value={k.zustaendig || ""} onChange={(e) => speichern.mutate({ zustaendig: e.target.value || null })}>
                        <option value="">niemand</option>
                        {Array.from(new Set([k.zustaendig, ...(zust.data?.items || [])].filter(Boolean) as string[])).map((n) => <option key={n}>{n}</option>)}
                      </select></div>
                    <div className="vt-feld breit"><label>Notiz</label>
                      <NotizFeld wert={k.notiz} speichere={(v) => speichern.mutate({ notiz: leer(v) })} /></div>
                  </div>
                </div>
              </section>

              {q.data!.verlauf.length > 0 && (
                <section className="vt-block">
                  <div className="vt-block-kopf"><h4>Verlauf aus allen Deals</h4></div>
                  <div className="vt-block-rumpf"><Verlauf eintraege={q.data!.verlauf} stufen={board.stufen} /></div>
                </section>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button className="vt-knopf leise klein" onClick={() => {
                  if (window.confirm(`„${kontaktName(k)}" löschen? Die Deals bleiben erhalten.`)) loeschen.mutate()
                }}><Trash2 /> Kontakt löschen</button>
              </div>
            </div>
          </>
        )}
      </aside>
    </div>,
    document.body,
  )
}

// ── Kontaktauswahl (im neuen Deal und am Deal) ───────────────────────────

/** Sucht einen bestehenden Kontakt. `null` = keiner gewählt. */
export function KontaktWahl({ gewaehlt, waehle, autoFocus }: {
  gewaehlt: Kontakt | null; waehle: (k: Kontakt | null) => void; autoFocus?: boolean
}) {
  const q = useKontakte()
  const [text, setText] = useState("")
  const [offen, setOffen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const klick = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOffen(false) }
    document.addEventListener("mousedown", klick)
    return () => document.removeEventListener("mousedown", klick)
  }, [])
  const treffer = useMemo(() => {
    const t = text.trim().toLowerCase()
    if (!t) return (q.data?.items || []).slice(0, 6)
    return (q.data?.items || []).filter((k) => [k.name, k.firma, k.email].some((x) => (x || "").toLowerCase().includes(t))).slice(0, 8)
  }, [q.data, text])

  if (gewaehlt) {
    return (
      <div className="vt-kontakt-chip">
        <Avatar k={gewaehlt} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <b>{kontaktName(gewaehlt)}</b>
          <span>{[gewaehlt.name && gewaehlt.firma, gewaehlt.email].filter(Boolean).join(" · ")}</span>
        </div>
        <button type="button" className="vt-ikon" title="Anderen Kontakt wählen" onClick={() => waehle(null)}><X /></button>
      </div>
    )
  }
  return (
    <div className="vt-kontakt-wahl" ref={box}>
      <label className="vt-suche" style={{ width: "100%", borderRadius: 10 }}>
        <Search />
        <input autoFocus={autoFocus} value={text} placeholder="Bestehenden Kontakt suchen …"
          onFocus={() => setOffen(true)} onChange={(e) => { setText(e.target.value); setOffen(true) }} />
      </label>
      {offen && treffer.length > 0 && (
        <ul className="vt-vorschlaege">
          {treffer.map((k) => (
            <li key={k.id}>
              <button type="button" onClick={() => { waehle(k); setOffen(false); setText("") }}>
                <Avatar k={k} />
                <span><b>{kontaktName(k)}</b>{k.name && k.firma ? <small> · {k.firma}</small> : null}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
